/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Storage Service
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Centralized storage management for:
 * - User profiles (natal + additional credit-based)
 * - Location presets
 * - Time presets
 * - Dial view history (tiered: session, week, month, year, starred)
 * - Synthesis saves
 * 
 * ARCHITECTURE:
 * - Uses ApiService for authenticated storage
 * - Falls back to localStorage for anonymous users
 * - Consistent save/load patterns
 * - Automatic sync between local and server
 */

import apiService from './ApiService';
import authService from './AuthService';
import { authLogger } from '../utils/logger';

// Local storage keys
const STORAGE_KEYS = {
  PROFILES: 'tod_profiles',
  LOCATION_PRESETS: 'tod_location_presets',
  TIME_PRESETS: 'tod_time_presets',
  SYNTHESIS_SAVES: 'tod_synthesis_saves',
  SESSION_HISTORY: 'tod_session_history',
};

// Tier configuration
const TIER_CONFIG = {
  session: { maxItems: 50, retentionDays: null },
  week: { maxItems: 50, retentionDays: 7 },
  month: { maxItems: 50, retentionDays: 30 },
  year: { maxItems: 50, retentionDays: 365 },
  starred: { maxItems: null, retentionDays: null }, // Unlimited, permanent
};

/**
 * Storage Service
 * Manages all persistent storage operations
 */
class StorageService {
  constructor() {
    if (StorageService.instance) {
      return StorageService.instance;
    }
    StorageService.instance = this;
  }
  
  // ============================================
  // PROFILES - Natal + Additional (credit-based)
  // ============================================
  
  /**
   * Get all additional profiles (not natal)
   * For authenticated users: fetches from server
   * For anonymous users: returns localStorage profiles
   */
  async getProfiles() {
    if (authService.isAuthenticated) {
      const response = await apiService.get('/auth/profiles');
      if (response.ok) {
        return response.data || [];
      }
      authLogger.error('Failed to fetch profiles:', response.error);
      return [];
    }
    
    // Fall back to localStorage
    return this.getLocalStorage(STORAGE_KEYS.PROFILES, []);
  }
  
  /**
   * Save a new profile
   * @param {Object} profileData - Profile data
   * @returns {Object} Saved profile with ID
   */
  async saveProfile(profileData) {
    if (authService.isAuthenticated) {
      const response = await apiService.post('/auth/profiles', profileData);
      if (!response.ok) {
        throw new Error(response.error || 'Failed to save profile');
      }
      return response.data;
    }
    
    // Local storage for anonymous users
    const profiles = this.getLocalStorage(STORAGE_KEYS.PROFILES, []);
    if (profiles.length >= 3) {
      throw new Error('Profile limit reached. Sign in to save more profiles.');
    }
    
    const newProfile = {
      profile_id: `local_${Date.now()}`,
      ...profileData,
      created_at: new Date().toISOString(),
    };
    
    profiles.push(newProfile);
    this.setLocalStorage(STORAGE_KEYS.PROFILES, profiles);
    return newProfile;
  }
  
  /**
   * Update an existing profile
   */
  async updateProfile(profileId, profileData) {
    if (authService.isAuthenticated) {
      const response = await apiService.put(`/auth/profiles/${profileId}`, profileData);
      if (!response.ok) {
        throw new Error(response.error || 'Failed to update profile');
      }
      return response.data;
    }
    
    // Local update
    const profiles = this.getLocalStorage(STORAGE_KEYS.PROFILES, []);
    const index = profiles.findIndex(p => p.profile_id === profileId);
    if (index !== -1) {
      profiles[index] = { ...profiles[index], ...profileData };
      this.setLocalStorage(STORAGE_KEYS.PROFILES, profiles);
      return profiles[index];
    }
    throw new Error('Profile not found');
  }
  
  /**
   * Delete a profile
   */
  async deleteProfile(profileId) {
    if (authService.isAuthenticated) {
      const response = await apiService.delete(`/auth/profiles/${profileId}`);
      if (!response.ok) {
        throw new Error(response.error || 'Failed to delete profile');
      }
      return true;
    }
    
    // Local delete
    const profiles = this.getLocalStorage(STORAGE_KEYS.PROFILES, []);
    const filtered = profiles.filter(p => p.profile_id !== profileId);
    this.setLocalStorage(STORAGE_KEYS.PROFILES, filtered);
    return true;
  }
  
  // ============================================
  // LOCATION PRESETS
  // ============================================
  
  /**
   * Helper to deduplicate locations by coordinates
   * Rounds to 2 decimal places to catch same location with minor variations
   */
  _deduplicateLocations(locations) {
    const seen = new Map();
    const seenIds = new Set();
    const result = [];
    
    for (const loc of locations) {
      if (!loc) continue;
      
      // Skip if we've seen this exact ID
      const id = loc.id || loc.preset_id;
      if (id && seenIds.has(id)) continue;
      
      // Create coordinate key (rounded to 2 decimals)
      const lat = parseFloat(loc.latitude || loc.lat || 0).toFixed(2);
      const lon = parseFloat(loc.longitude || loc.lon || loc.lng || 0).toFixed(2);
      const coordKey = `${lat}_${lon}`;
      
      if (seen.has(coordKey)) continue;
      
      if (id) seenIds.add(id);
      seen.set(coordKey, loc);
      result.push(loc);
    }
    
    return result;
  }
  
  /**
   * Get location presets - deduplicated
   */
  async getLocationPresets() {
    let presets = [];
    
    if (authService.isAuthenticated) {
      const response = await apiService.get('/auth/location-presets');
      if (response.ok) {
        presets = response.data?.presets || [];
      }
    } else {
      presets = this.getLocalStorage(STORAGE_KEYS.LOCATION_PRESETS, []);
    }
    
    // Always return deduplicated presets
    return this._deduplicateLocations(presets);
  }
  
  /**
   * Save location presets (replaces all) - deduplicates before saving
   */
  async saveLocationPresets(presets) {
    // Deduplicate before saving
    const dedupedPresets = this._deduplicateLocations(presets);
    
    if (authService.isAuthenticated) {
      const response = await apiService.put('/auth/location-presets', { presets: dedupedPresets });
      if (!response.ok) {
        throw new Error(response.error || 'Failed to save location presets');
      }
      return response.data;
    }
    
    this.setLocalStorage(STORAGE_KEYS.LOCATION_PRESETS, dedupedPresets);
    return { presets: dedupedPresets };
  }
  
  /**
   * Add a single location preset - with deduplication
   */
  async addLocationPreset(preset) {
    const presets = await this.getLocationPresets();
    const newPreset = {
      ...preset,
      id: preset.id || `loc_${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    
    // Deduplicate the combined list
    const combined = [...presets, newPreset];
    const dedupedPresets = this._deduplicateLocations(combined);
    
    return this.saveLocationPresets(dedupedPresets);
  }
  
  /**
   * Remove a location preset
   */
  async removeLocationPreset(presetId) {
    const presets = await this.getLocationPresets();
    const filtered = presets.filter(p => p.id !== presetId);
    return this.saveLocationPresets(filtered);
  }
  
  // ============================================
  // TIME PRESETS - localStorage + backend sync
  // ============================================
  
  /**
   * Get time presets
   * For authenticated users: fetches from server, updates localStorage
   * For anonymous: returns localStorage
   */
  async getTimePresets() {
    if (authService.isAuthenticated) {
      const response = await apiService.get('/auth/time-presets');
      if (response.ok) {
        const presets = response.data?.presets || [];
        // Sync to localStorage
        this.setLocalStorage(STORAGE_KEYS.TIME_PRESETS, presets);
        return presets;
      }
    }
    return this.getLocalStorage(STORAGE_KEYS.TIME_PRESETS, []);
  }
  
  /**
   * Save time presets (replaces all)
   */
  async saveTimePresets(presets) {
    // Always save to localStorage first
    this.setLocalStorage(STORAGE_KEYS.TIME_PRESETS, presets);
    
    // Sync to backend if authenticated
    if (authService.isAuthenticated) {
      const response = await apiService.put('/auth/time-presets', { presets });
      if (!response.ok) {
        authLogger.warn('Failed to sync time presets to backend:', response.error);
      }
    }
    return presets;
  }
  
  /**
   * Add a time preset
   */
  async addTimePreset(preset) {
    const presets = await this.getTimePresets();
    const newPreset = {
      ...preset,
      id: `time_${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    presets.push(newPreset);
    await this.saveTimePresets(presets);
    return newPreset;
  }
  
  /**
   * Remove a time preset
   */
  async removeTimePreset(presetId) {
    const presets = await this.getTimePresets();
    const filtered = presets.filter(p => p.id !== presetId);
    await this.saveTimePresets(filtered);
    return filtered;
  }
  
  /**
   * Update a time preset
   */
  async updateTimePreset(presetId, updates) {
    const presets = await this.getTimePresets();
    const index = presets.findIndex(p => p.id === presetId);
    if (index !== -1) {
      presets[index] = { ...presets[index], ...updates };
      await this.saveTimePresets(presets);
    }
    return presets;
  }
  
  // ============================================
  // DIAL VIEW HISTORY (Tiered Storage)
  // ============================================
  
  /**
   * Get all dial view history grouped by tier
   * Requires authentication
   */
  async getDialViewHistory() {
    if (!authService.isAuthenticated) {
      return {
        tiers: { session: [], week: [], month: [], year: [], starred: [] },
        counts: { session: 0, week: 0, month: 0, year: 0, starred: 0 },
        limits: TIER_CONFIG,
      };
    }
    
    // Get session retention preference from localStorage (default 24 hours)
    const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
    const response = await apiService.get(`/history?session_retention_hours=${sessionRetentionHours}`);
    if (response.ok) {
      return response.data;
    }
    
    authLogger.error('Failed to fetch dial view history:', response.error);
    return {
      tiers: { session: [], week: [], month: [], year: [], starred: [] },
      counts: {},
      limits: TIER_CONFIG,
    };
  }
  
  /**
   * Save a dial view to history
   * @param {Object} viewState - The dial state to save
   * @param {string} label - Optional label
   * @param {string} tier - Storage tier
   */
  async saveDialView(viewState, label = null, tier = 'session') {
    if (!authService.isAuthenticated) {
      throw new Error('Authentication required to save dial views');
    }
    
    // Get session retention preference from localStorage (default 24 hours)
    const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
    
    const response = await apiService.post('/history', {
      view_state: viewState,
      label,
      tier,
      session_retention_hours: sessionRetentionHours,
    });
    
    if (!response.ok) {
      throw new Error(response.error || 'Failed to save dial view');
    }
    
    return response.data;
  }
  
  /**
   * Update dial view entry (move tier, star, rename)
   */
  async updateDialView(entryId, updates) {
    // Get session retention preference from localStorage (default 24 hours)
    const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
    
    const response = await apiService.put(`/history/${entryId}`, {
      ...updates,
      session_retention_hours: sessionRetentionHours,
    });
    if (!response.ok) {
      throw new Error(response.error || 'Failed to update dial view');
    }
    return response.data;
  }
  
  /**
   * Delete dial view entry
   */
  async deleteDialView(entryId) {
    const response = await apiService.delete(`/history/${entryId}`);
    if (!response.ok) {
      throw new Error(response.error || 'Failed to delete dial view');
    }
    return true;
  }
  
  /**
   * Toggle star status
   */
  async toggleDialViewStar(entryId, currentlyStarred) {
    return this.updateDialView(entryId, {
      starred: !currentlyStarred,
      tier: !currentlyStarred ? 'starred' : 'session',
    });
  }
  
  // ============================================
  // SYNTHESIS SAVES
  // ============================================
  
  /**
   * Get all synthesis saves
   */
  async getSynthesisSaves() {
    if (authService.isAuthenticated) {
      // Get session retention preference from localStorage (default 24 hours)
      const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
      const response = await apiService.get(`/history/synthesis?session_retention_hours=${sessionRetentionHours}`);
      if (response.ok) {
        return response.data;
      }
    }
    
    // Fall back to localStorage
    const localSaves = this.getLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, []);
    return this.formatSynthesisAsGrouped(localSaves);
  }
  
  /**
   * Save a synthesis
   */
  async saveSynthesis(synthesisData) {
    if (authService.isAuthenticated) {
      // Get session retention preference from localStorage (default 24 hours)
      const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
      const response = await apiService.post('/history/synthesis', {
        ...synthesisData,
        session_retention_hours: sessionRetentionHours,
      });
      if (response.ok) {
        return response.data;
      }
    }
    
    // Fall back to localStorage
    const saves = this.getLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, []);
    const newSave = {
      id: `synth_${Date.now()}`,
      synthesis_id: `synth_${Date.now()}`,
      ...synthesisData,
      tier: 'session',
      starred: false,
      created_at: new Date().toISOString(),
    };
    saves.push(newSave);
    this.setLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, saves);
    return newSave;
  }
  
  /**
   * Update synthesis (move tier, star)
   */
  async updateSynthesis(synthesisId, updates) {
    if (authService.isAuthenticated) {
      // Get session retention preference from localStorage (default 24 hours)
      const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '24', 10);
      const response = await apiService.put(`/history/synthesis/${synthesisId}`, {
        ...updates,
        session_retention_hours: sessionRetentionHours,
      });
      if (response.ok) {
        return response.data;
      }
    }
    
    // Update localStorage
    const saves = this.getLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, []);
    const index = saves.findIndex(s => s.id === synthesisId || s.synthesis_id === synthesisId);
    if (index !== -1) {
      saves[index] = { ...saves[index], ...updates };
      this.setLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, saves);
      return saves[index];
    }
    throw new Error('Synthesis not found');
  }
  
  /**
   * Delete synthesis
   */
  async deleteSynthesis(synthesisId) {
    if (authService.isAuthenticated) {
      const response = await apiService.delete(`/history/synthesis/${synthesisId}`);
      if (response.ok) {
        return true;
      }
    }
    
    // Delete from localStorage
    const saves = this.getLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, []);
    const filtered = saves.filter(s => s.id !== synthesisId && s.synthesis_id !== synthesisId);
    this.setLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, filtered);
    return true;
  }
  
  /**
   * Format flat synthesis array as grouped by tier
   */
  formatSynthesisAsGrouped(saves) {
    const grouped = {
      session: saves.filter(s => !s.starred && (!s.tier || s.tier === 'session')),
      week: saves.filter(s => s.tier === 'week'),
      month: saves.filter(s => s.tier === 'month'),
      year: saves.filter(s => s.tier === 'year'),
      starred: saves.filter(s => s.starred || s.tier === 'starred'),
    };
    
    return {
      tiers: grouped,
      counts: {
        session: grouped.session.length,
        week: grouped.week.length,
        month: grouped.month.length,
        year: grouped.year.length,
        starred: grouped.starred.length,
      },
      limits: { total: 50 },
    };
  }
  
  /**
   * Get syntheses storage stats (for UI display)
   * Fetches limits from backend API (same pattern as notes)
   * Note: "Syntheses" is the correct plural form of "Synthesis"
   */
  async getSynthesesStorageStats() {
    try {
      // Fetch synthesis data from backend - includes user-specific limits
      const response = await apiService.get('/history/synthesis');
      
      // Check if response is ok and extract data (same pattern as getNotes)
      if (!response.ok) {
        throw new Error(response.error || 'Failed to fetch synthesis data');
      }
      
      const data = response.data;
      
      // Get starred count and limit (matching notes pattern)
      const starredCount = data.counts?.starred || 0;
      const starredLimit = data.limits?.starred;
      
      // Check if unlimited (admin/VII/insider users get "unlimited" from backend)
      const isUnlimited = starredLimit === "unlimited" || starredLimit === null || starredLimit === "∞";
      
      // Calculate total across all tiers for display
      const totalSaves = data.counts 
        ? Object.values(data.counts).reduce((acc, count) => acc + (count || 0), 0)
        : 0;
      
      return {
        synthesesCount: totalSaves,
        starredCount: starredCount,
        maxSyntheses: isUnlimited ? "unlimited" : starredLimit,
        remainingSlots: isUnlimited ? "∞ unlimited" : Math.max(0, (starredLimit || 50) - starredCount),
        isUnlimited
      };
    } catch (err) {
      authLogger.error('Failed to get syntheses storage stats:', err);
      // Fallback to localStorage
      const MAX_FREE_SYNTHESES = 50;
      const saves = this.getLocalStorage(STORAGE_KEYS.SYNTHESIS_SAVES, []);
      
      return {
        synthesesCount: saves.length,
        starredCount: 0,
        maxSyntheses: MAX_FREE_SYNTHESES,
        remainingSlots: MAX_FREE_SYNTHESES - saves.length,
        isUnlimited: false
      };
    }
  }
  
  // Alias for backwards compatibility
  async getSynthesisStorageStats() {
    return this.getSynthesesStorageStats();
  }
  
  /**
   * Get notes storage stats for UI display
   * Returns current count, limit, and remaining slots
   * Accounts for user tier (admin/VII = unlimited)
   */
  async getNotesStorageStats() {
    try {
      const notesData = await this.getNotes();
      const starredCount = notesData.counts?.starred || 0;
      const starredLimit = notesData.limits?.starred;
      
      // Check if unlimited (admin/VII users)
      const isUnlimited = starredLimit === "unlimited" || starredLimit === null;
      
      return {
        count: starredCount,
        limit: isUnlimited ? "unlimited" : starredLimit,
        isUnlimited,
        remainingSlots: isUnlimited ? Infinity : Math.max(0, starredLimit - starredCount)
      };
    } catch (err) {
      authLogger.error('Failed to get notes storage stats:', err);
      // Default to base limit of 20 on error
      return {
        count: 0,
        limit: 20,
        isUnlimited: false,
        remainingSlots: 20
      };
    }
  }
  
  // ============================================
  // LOCAL STORAGE UTILITIES
  // ============================================
  
  getLocalStorage(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (err) {
      authLogger.error(`Failed to read localStorage key ${key}:`, err);
      return defaultValue;
    }
  }
  
  setLocalStorage(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      authLogger.error(`Failed to write localStorage key ${key}:`, err);
    }
  }
  
  removeLocalStorage(key) {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      authLogger.error(`Failed to remove localStorage key ${key}:`, err);
    }
  }
  
  /**
   * Sync local storage data to server after login
   * Merges local profiles/presets with server data
   */
  async syncLocalToServer() {
    if (!authService.isAuthenticated) return;
    
    try {
      // Sync local profiles
      const localProfiles = this.getLocalStorage(STORAGE_KEYS.PROFILES, []);
      for (const profile of localProfiles) {
        if (profile.profile_id.startsWith('local_')) {
          await this.saveProfile(profile);
        }
      }
      
      // Clear local profiles after sync
      this.removeLocalStorage(STORAGE_KEYS.PROFILES);
      
      // Sync local location presets - deduplicate before saving
      const localLocations = this.getLocalStorage(STORAGE_KEYS.LOCATION_PRESETS, []);
      if (localLocations.length > 0) {
        const serverLocations = await this.getLocationPresets();
        
        // Deduplicate: prefer server items, add local items only if not already present
        const serverIds = new Set(serverLocations.map(l => l.id));
        const serverCities = new Set(serverLocations.map(l => `${l.city}_${l.latitude}_${l.longitude}`));
        
        const uniqueLocalLocations = localLocations.filter(loc => {
          // Skip if ID already exists on server
          if (loc.id && serverIds.has(loc.id)) return false;
          // Skip if same city/coordinates already exists
          const key = `${loc.city}_${loc.latitude}_${loc.longitude}`;
          if (serverCities.has(key)) return false;
          return true;
        });
        
        const merged = [...serverLocations, ...uniqueLocalLocations];
        await this.saveLocationPresets(merged);
        this.removeLocalStorage(STORAGE_KEYS.LOCATION_PRESETS);
      }
      
      authLogger.log('Local data synced to server');
    } catch (err) {
      authLogger.error('Failed to sync local data:', err);
    }
  }
  
  // ============================================
  // RECENT LOCATIONS (localStorage only, auto-tracked)
  // ============================================
  
  /**
   * Get recent locations (for location search suggestions)
   */
  getRecentLocations(limit = 10) {
    const locations = this.getLocalStorage('tod_recent_locations', []);
    return locations.slice(0, limit);
  }
  
  /**
   * Add a location to recent locations
   */
  addRecentLocation(location) {
    if (!location?.city || location.latitude === undefined) return;
    
    const recent = this.getRecentLocations(20);
    
    const newLocation = {
      city: location.city,
      latitude: location.latitude,
      longitude: location.longitude,
      country: location.country || '',
      region: location.region || location.state || '',
      timezone: location.timezone || '',
      timestamp: Date.now()
    };
    
    // Remove duplicate
    const filtered = recent.filter(loc => 
      !(loc.city === newLocation.city && loc.country === newLocation.country)
    );
    
    // Add to front, limit to 10
    const updated = [newLocation, ...filtered].slice(0, 10);
    this.setLocalStorage('tod_recent_locations', updated);
  }
  
  /**
   * Clear recent locations
   */
  clearRecentLocations() {
    this.removeLocalStorage('tod_recent_locations');
  }
  
  /**
   * Filter recent locations based on search query
   * @param {string} query - Search query
   * @returns {Array} Filtered locations
   */
  filterRecentLocations(query) {
    if (!query || query.trim().length === 0) {
      return this.getRecentLocations();
    }
    
    const lowerQuery = query.toLowerCase().trim();
    return this.getRecentLocations(20).filter(loc => 
      loc.city?.toLowerCase().includes(lowerQuery) ||
      loc.country?.toLowerCase().includes(lowerQuery) ||
      loc.region?.toLowerCase().includes(lowerQuery)
    );
  }
  
  // ============================================
  // NOTES MANAGEMENT
  // ============================================
  
  /**
   * Get all notes grouped by tier
   */
  async getNotes() {
    if (!authService.isAuthenticated) {
      // Return local-only notes for non-authenticated users
      const localNotes = this.getLocalStorage('tod_local_notes', []);
      return {
        tiers: {
          session: localNotes,
          week: [],
          month: [],
          year: [],
          starred: []
        },
        counts: { session: localNotes.length, week: 0, month: 0, year: 0, starred: 0 },
        limits: { session: 5, week: 5, month: 5, year: 5, starred: 20 }
      };
    }
    
    try {
      // Pass session retention hours for migration of old notes
      const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '36', 10);
      const response = await apiService.get(`/history/notes?session_retention_hours=${sessionRetentionHours}`);
      if (response.ok) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch notes');
    } catch (err) {
      authLogger.error('Failed to fetch notes:', err);
      return {
        tiers: { session: [], week: [], month: [], year: [], starred: [] },
        counts: { session: 0, week: 0, month: 0, year: 0, starred: 0 },
        limits: { session: 5, week: 5, month: 5, year: 5, starred: 20 }
      };
    }
  }
  
  /**
   * Save a new note
   * @param {Object} noteData - Note data including note_text, context, synthesis info
   * @param {string} tier - Storage tier (session, week, month, year, starred)
   */
  async saveNote(noteData, tier = 'session') {
    // Get session retention preference from localStorage (default 36 hours)
    const sessionRetentionHours = parseInt(localStorage.getItem('tod_auto_delete_hours') || '36', 10);
    
    const noteEntry = {
      note_text: noteData.note_text,
      label: noteData.label || this.generateNoteLabel(noteData),
      synthesis_type: noteData.synthesis_type,
      synthesis_key: noteData.synthesis_key,
      context: noteData.context || {},
      tier,
      session_retention_hours: sessionRetentionHours  // Pass user's preference for session expiry
    };
    
    if (!authService.isAuthenticated) {
      // Save locally for non-authenticated users
      const localNotes = this.getLocalStorage('tod_local_notes', []);
      const localEntry = {
        ...noteEntry,
        note_id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        starred: tier === 'starred',
        created_at: new Date().toISOString()
      };
      localNotes.unshift(localEntry);
      // Limit local notes to 10
      if (localNotes.length > 10) localNotes.pop();
      this.setLocalStorage('tod_local_notes', localNotes);
      return localEntry;
    }
    
    try {
      const response = await apiService.post('/history/notes', noteEntry);
      if (response.ok) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to save note');
    } catch (err) {
      authLogger.error('Failed to save note:', err);
      throw err;
    }
  }
  
  /**
   * Update a note (move tier, edit text, rename)
   */
  async updateNote(noteId, updates) {
    if (!authService.isAuthenticated) {
      // Update locally for non-authenticated users
      const localNotes = this.getLocalStorage('tod_local_notes', []);
      const idx = localNotes.findIndex(n => n.note_id === noteId);
      if (idx !== -1) {
        localNotes[idx] = { ...localNotes[idx], ...updates };
        this.setLocalStorage('tod_local_notes', localNotes);
        return localNotes[idx];
      }
      throw new Error('Note not found');
    }
    
    try {
      const response = await apiService.put(`/history/notes/${noteId}`, updates);
      if (response.ok) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to update note');
    } catch (err) {
      authLogger.error('Failed to update note:', err);
      throw err;
    }
  }
  
  /**
   * Delete a note
   */
  async deleteNote(noteId) {
    if (!authService.isAuthenticated) {
      // Delete locally for non-authenticated users
      const localNotes = this.getLocalStorage('tod_local_notes', []);
      const filtered = localNotes.filter(n => n.note_id !== noteId);
      this.setLocalStorage('tod_local_notes', filtered);
      return { success: true };
    }
    
    try {
      const response = await apiService.delete(`/history/notes/${noteId}`);
      if (response.ok) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to delete note');
    } catch (err) {
      authLogger.error('Failed to delete note:', err);
      throw err;
    }
  }
  
  /**
   * Toggle star status of a note
   */
  async toggleNoteStar(noteId, currentlyStarred) {
    const newTier = currentlyStarred ? 'session' : 'starred';
    return this.updateNote(noteId, { tier: newTier });
  }
  
  /**
   * Move note to a different tier
   */
  async moveNoteToTier(noteId, newTier) {
    return this.updateNote(noteId, { tier: newTier });
  }
  
  /**
   * Generate a label for a note based on context
   */
  generateNoteLabel(noteData) {
    if (noteData.synthesis_type) {
      const typeLabels = {
        general: 'General',
        personal: 'Personal',
        cosmic_climate: 'Cosmic Climate',
        relationship: 'Relationship',
        place_specific: 'Place-Specific'
      };
      const type = typeLabels[noteData.synthesis_type] || noteData.synthesis_type;
      const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${type} Note - ${date}`;
    }
    return `Note - ${new Date().toLocaleDateString()}`;
  }
  
  // ============================================
  // OFFLINE RETRY QUEUE (Simple)
  // ============================================
  
  /**
   * Queue a failed operation for retry
   */
  queueForRetry(operation) {
    const queue = this.getLocalStorage('tod_retry_queue', []);
    queue.push({
      ...operation,
      queuedAt: new Date().toISOString(),
      retryCount: 0
    });
    this.setLocalStorage('tod_retry_queue', queue);
  }
  
  /**
   * Process retry queue (call on app init and when online)
   */
  async processRetryQueue() {
    if (!authService.isAuthenticated) return;
    
    const queue = this.getLocalStorage('tod_retry_queue', []);
    if (queue.length === 0) return;
    
    const remainingQueue = [];
    
    for (const op of queue) {
      try {
        let response;
        switch (op.type) {
          case 'saveSynthesis':
            response = await apiService.post('/history/synthesis', op.data);
            break;
          case 'saveDialView':
            response = await apiService.post('/history', op.data);
            break;
          case 'saveLocationPreset':
            response = await apiService.put('/auth/location-presets', { presets: op.data });
            break;
          case 'saveTimePreset':
            response = await apiService.put('/auth/time-presets', { presets: op.data });
            break;
          case 'saveNote':
            response = await apiService.post('/history/notes', op.data);
            break;
          default:
            continue;
        }
        
        if (!response?.ok && op.retryCount < 3) {
          op.retryCount++;
          remainingQueue.push(op);
        }
      } catch (err) {
        if (op.retryCount < 3) {
          op.retryCount++;
          remainingQueue.push(op);
        }
      }
    }
    
    this.setLocalStorage('tod_retry_queue', remainingQueue);
    
    if (remainingQueue.length < queue.length) {
      authLogger.log(`Retry queue: ${queue.length - remainingQueue.length} operations succeeded`);
    }
  }
  
  // ============================================
  // STORAGE QUOTA INFO
  // ============================================
  
  /**
   * Get storage usage info for the current user
   */
  async getStorageQuota() {
    const dialViews = await this.getDialViewHistory();
    const synthesis = await this.getSynthesisSaves();
    const profiles = await this.getProfiles();
    const locationPresets = await this.getLocationPresets();
    const timePresets = await this.getTimePresets();
    
    return {
      dialViews: {
        used: Object.values(dialViews.counts || {}).reduce((a, b) => a + b, 0),
        limits: dialViews.limits
      },
      synthesis: {
        used: Object.values(synthesis.counts || {}).reduce((a, b) => a + b, 0),
        limits: synthesis.limits
      },
      profiles: {
        used: profiles.length,
        limit: authService.maxProfiles
      },
      locationPresets: {
        used: locationPresets.length
      },
      timePresets: {
        used: timePresets.length
      }
    };
  }
}

// Export singleton instance
const storageService = new StorageService();
export default storageService;
